import React, { useContext } from "react"
import PropTypes from "prop-types"
import { InputText } from "../common/forms"
import {
  validateName,
  validateCardNumber,
  normalizeName,
  normalizeCardExpiration,
  normalizeCardCode,
  normalizeCardNumber,
} from "../helpers/formHelper"

import {
  isTourOperator,
  isBookingAgent,
  isBookingAgentAndisBookingAnUpgrade,
  isBookingAgentAndisBookingAfresh,
  canChargeUpgradeToCreditLimit
} from "../helpers/userHelper"

import { SearchContext } from '../search/SearchContext'

const PaymentFields = ({
  handler,
  errors,
  values,
  existingCard,
  useExistingCard,
  toggleUseExistingCard,
  saveCard,
  toggleSaveCard,
  addSurchargeToCreditLine,
  toggleAddSurchargeToCreditLine,
  chargePaymentToCreditLine,
  toggleChargePaymentToCreditLine,
  user,
  room,
  upgradeType,
  setUpgradeType
}) => {
  const { state } = useContext(SearchContext)

  const onSaveCardClick = (e) => {
    e.preventDefault()
    toggleSaveCard()
  }

  const onAddSurchargeToCreditLineClick = (e) => {
    e.preventDefault()
    toggleAddSurchargeToCreditLine()
  }

  const onChargePaymentToCardClick = (e) => {
    e.preventDefault()
    toggleChargePaymentToCreditLine()
  }

  const _displayExistingFields = () => {
    return (
      <React.Fragment>
        <h3>Payment Details</h3>
        <div className="fieldset">
          <h3 style={{ width: "100%" }}>
            Use Card XXXX-XXXX-XXXX-{existingCard}
            <a
              style={{ float: "right" }}
              onClick={(e) => toggleUseExistingCard()}
            >
              Enter New Card?
            </a>
          </h3>
          <InputText
            className={errors.cvc && "error"}
            type="text"
            name="cvc"
            label="CVC"
            value={values.cvc}
            handler={handler}
            required={true}
            normalize={normalizeCardCode}
            placeholder="123"
          />
        </div>
      </React.Fragment>
    )
  }

  const _displayPaymentFieldsForNormalUser = () => {
    return (
      <React.Fragment>
        <h3>
          Payment Details
          {existingCard && (
            <a
              style={{ float: "right" }}
              onClick={(e) => toggleUseExistingCard()}
            >
              Use Existing Card?
            </a>
          )}
        </h3>
        <div className="fieldset">
          {_displayPaymentFields()}
          <div className="interactive rows rooms">
            <button
              className={`row room check-box ${saveCard ? "selected" : ""}`}
              onClick={(e) => onSaveCardClick(e)}
            >
              <span className="name">Save card to file?</span>
            </button>
          </div>
        </div>
      </React.Fragment>
    )
  }

  const onUpgradeTypeClicked = (e, upgradeName) => {
    e.preventDefault()
    setUpgradeType(upgradeName)
  }

  const upgradeTypes = [
    'Property Allotment',
    'Premium Property',
    'Additional Night(s)',
    'Misc. Fee'
  ]

  const upgradeTypeIsSelected = (type) => {
    return upgradeType === type
  }

  const _displayUpgradefields = () => {
    if (!isBookingAgentAndisBookingAnUpgrade(state)) {
      return
    }
    return (
      <React.Fragment>
        <div className="interactive rows rooms">
          <button
            className={`row room check-box ${
              upgradeTypeIsSelected(upgradeTypes[0]) ? "selected" : ""
            }`}
            onClick={(e) => onUpgradeTypeClicked(e, upgradeTypes[0])}
          >
            <span className="name">{upgradeTypes[0]}</span>
          </button>
          <button
            className={`row room check-box ${
              upgradeTypeIsSelected(upgradeTypes[1]) ? "selected" : ""
            }`}
            onClick={(e) => onUpgradeTypeClicked(e, upgradeTypes[1])}
          >
            <span className="name">{upgradeTypes[1]}</span>
          </button>
          <button
            className={`row room check-box ${
              upgradeTypeIsSelected(upgradeTypes[2]) ? "selected" : ""
            }`}
            onClick={(e) => onUpgradeTypeClicked(e, upgradeTypes[2])}
          >
            <span className="name">{upgradeTypes[2]}</span>
          </button>
          <button
            className={`row room check-box ${
              upgradeTypeIsSelected(upgradeTypes[3]) ? "selected" : ""
            }`}
            onClick={(e) => onUpgradeTypeClicked(e, upgradeTypes[3])}
          >
            <span className="name">{upgradeTypes[3]}</span>
          </button>
        </div>
      </React.Fragment>
    )
  }

  const _displayPaymentFieldsForTourOperator = () => {
    return (
      <React.Fragment>
        <h3>Payment Details</h3>
        <div className="fieldset">
          {_displayPaymentFields()}
          {_displayUpgradefields()}
          {isBookingAgentAndisBookingAfresh(state) && _displayChargePaymentToCard()}
          {canChargeUpgradeToCreditLimit(state) && (
            <div className="interactive rows rooms">
              <button
                className={`row room check-box ${
                  addSurchargeToCreditLine ? "selected" : ""
                }`}
                onClick={(e) => onAddSurchargeToCreditLineClick(e)}
              >
                <span className="name">
                  Charge the upgrade amount of ${room.rates[0].surcharge} to the
                  credit line
                </span>
              </button>
            </div>
          )}
        </div>
      </React.Fragment>
    )
  }

  const _displayChargePaymentToCard = () => {
    return (
      <div className="interactive rows rooms">
        <button
          className={`row room check-box ${
            chargePaymentToCreditLine ? "selected" : ""
          }`}
          onClick={(e) => onChargePaymentToCardClick(e)}
        >
          <span className="name">Charge the payment to the credit line</span>
        </button>
      </div>
    )
  }

  const _displayPaymentFields = () => {
    return (
      <React.Fragment>
        <InputText
          className={errors.cardName && "error"}
          type="text"
          name="cardName"
          label="Name on card"
          value={values.cardName}
          handler={handler}
          required={true}
          normalize={normalizeName}
          validate={validateName}
          placeholder="John Smith"
        />

        <InputText
          className={errors.cardNumber && "error"}
          type="text"
          inputmode = "numeric"
          pattern   = "[0-9]*"
          name="cardNumber"
          label="Card Number"
          value={values.cardNumber}
          handler={handler}
          required={true}
          normalize={normalizeCardNumber}
          validate={validateCardNumber}
          placeholder="xxxx-xxxx-xxxx-xxxx"
        />
        <InputText
          className={errors.expiryDate && "error"}
          type="text"
          inputmode = "numeric"
          pattern   = "[0-9]*"
          name="expiryDate"
          label="Expiration Date"
          value={values.expiryDate}
          handler={handler}
          required={true}
          normalize={normalizeCardExpiration}
          placeholder="MM/YY"
        />
        <InputText
          className={errors.cvc && "error"}
          type="text"
          inputmode = "numeric"
          pattern   = "[0-9]*"
          name="cvc"
          label="Security Code"
          value={values.cvc}
          handler={handler}
          required={true}
          normalize={normalizeCardCode}
          placeholder="123"
        />
      </React.Fragment>
    )
  }

  if (isTourOperator(user) || isBookingAgent(user)) {
    return _displayPaymentFieldsForTourOperator()
  } else {
    if (existingCard && useExistingCard) {
      return _displayExistingFields()
    } else {
      return _displayPaymentFieldsForNormalUser()
    }
  }
}

export default PaymentFields
